import React, { useEffect, useState } from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { useBalanceAdjustments } from '../../Data/Admin';
import { PageLoading } from '../../Components/Loading';
import DateTime from '../../Components/Formatting/DateTime';
import Money from '../../Components/Formatting/Money';
import Icon from '../../Components/Icons/Icon';
import Modal from '../../Components/Modal';
import Form, { Actions, Radio, SwitchGroup } from '../../Components/Form';
import Button from '../../Components/Button/Button';
import ButtonRow from '../../Components/Button/ButtonRow';
import { months } from '../../Data/Misc';
import { usePreference } from '../../Data/Preference';
import NoValue from '../../Components/EmptyStates/NoValue';

const Revenue = () => {
    const [args, setArgs] = usePreference('adminRevenueArgs', {
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        includeTestAccounts: false,
        includeNonSoapyRevenue: false,
        includeAnyChangePence: false,
        download: false,
    });

    const {summary, expected, adjustments, refresh} = useBalanceAdjustments(args);
    const [adjustOpen, setAdjustOpen] = useState(false);

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [args]);

    return <>
        <TopBar left="/admin" title="Revenue" />
        <Page>
            {summary ? <Inner {...{summary, expected, adjustments, setAdjustOpen, args, setArgs}} /> : <PageLoading />}
        </Page>
        <Adjust {...{args, setArgs, adjustOpen, setAdjustOpen, refresh}} />
    </>
}

const Inner = ({summary, expected, adjustments, setAdjustOpen, args, setArgs}) => {
    return <>
        <Summary {...{summary, expected, setAdjustOpen, args, setArgs}} />
        <Adjustments {...{adjustments}} />
    </>;
}

const Summary = ({summary, expected, setAdjustOpen, args, setArgs}) => {
    const thisMonth = new Date();
    const lastMonth = new Date();
    const monthBefore = new Date();
    lastMonth.setMonth(thisMonth.getMonth() - 1);
    monthBefore.setMonth(thisMonth.getMonth() - 2);

    const changeMonth = (date) => {
        setArgs({
            ...args,
            year: date.getFullYear(),
            month: date.getMonth() + 1
        });
    }

    const isSelectedMonth = (date) => {
        return date.getFullYear() === args.year && date.getMonth() === args.month - 1;
    }

    const cellClass = 'px-2 py-1';

    return <div>
        <div className="my-4">
            <ButtonRow arrange="around">
                <Button label={months[monthBefore.getMonth()]}
                    theme="light"
                    icon={isSelectedMonth(monthBefore) ? 'Check' : null}
                    onClick={() => changeMonth(monthBefore)}
                    zeroMargin
                />
                <Button label={months[lastMonth.getMonth()]}
                    theme="light"
                    icon={isSelectedMonth(lastMonth) ? 'Check' : null}
                    onClick={() => changeMonth(lastMonth)}
                    zeroMargin
                />
                <Button label={months[thisMonth.getMonth()]}
                    theme="light"
                    icon={isSelectedMonth(thisMonth) ? 'Check' : null}
                    onClick={() => changeMonth(thisMonth)}
                    zeroMargin
                />
                <Button label="Custom" theme="primary" onClick={() => setAdjustOpen(true)} zeroMargin />
            </ButtonRow>
        </div>
        <div className="my-4 border rounded-lg">
            <table className="w-full table-auto">
                <thead>
                    <tr>
                        <th className="text-left bg-blue-50 p-2 border-b rounded-tl-lg">Summary</th>
                        <th className="text-left bg-blue-50 p-2 border-b">Total</th>
                        <th className="text-left bg-blue-50 p-2 border-b rounded-tr-lg">15%</th>
                    </tr>
                </thead>
                <tbody>
                    <tr className="border-b">
                        <td className={cellClass}>All Revenue</td>
                        <td className={cellClass}><Money pence={summary.soapyRevenuePence ?? 0} /></td>
                        <td className={cellClass}><Money pence={Math.round(summary.soapyRevenuePence * 0.15)} /></td>
                    </tr>
                    <tr className="bg-gray-50">
                        <td className={cellClass}>Subscription</td>
                        <td className={cellClass}>
                            <Money pence={summary.subscriptionPence ?? 0} />
                            <span className="text-muted">&nbsp; ({summary.subscriptionCount})</span>
                        </td>
                        <td className={cellClass}><Money pence={Math.round(summary.subscriptionPence * 0.15)} /></td>
                    </tr>
                    <tr className="bg-gray-50">
                        <td className={cellClass}>SMS Bundles</td>
                        <td className={cellClass}>
                            <Money pence={summary.SMSBundlePence ?? 0} />
                            <span className="text-muted">&nbsp; ({summary.SMSBundleCount} / {summary.SMSBundleSum})</span>
                        </td>
                        <td className={cellClass}><Money pence={Math.round(summary.SMSBundlePence * 0.15)} /></td>
                    </tr>
                    <tr className="border-t text-muted">
                        <td className={cellClass}>Refunds</td>
                        <td className={cellClass}>
                            <Money pence={summary.refundPence ?? 0} />
                            <span className="text-muted">&nbsp; ({summary.refundCount})</span>
                        </td>
                        <td className={cellClass}><Money pence={Math.round(summary.refundPence * 0.15)} /></td>
                    </tr>
                    <tr>
                        <td className={`${cellClass} text-muted`}>Expected</td>
                        <td className={`${cellClass} text-muted`}>
                            <Money pence={Math.round(expected.total)} />
                            <span className="text-muted">&nbsp; ({expected.count})</span>
                        </td>
                        <td className={`${cellClass} text-muted`}><Money pence={Math.round(expected.total * 0.15)} /></td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
}

const Adjustments = ({adjustments}) => {
    const cellClass = "whitespace-nowrap px-3 py-4 text-sm text-gray-500";

    return <div className="p-2">
        <table className="min-w-full divide-y divide-gray-300 text-center">
            <thead>
                <tr>
                    <th>Test</th>
                    <th>Event</th>
                    <th>Funded</th>
                    <th>Date</th>
                    <th>Count</th>
                    <th>Value</th>
                    <th>Revenue</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
                {adjustments.map(adjustment => {
                    const negative = adjustment.type === 'RetainedBalanceWasRefunded';
                    const revPence = negative ? -adjustment.soapyRevenuePence : adjustment.soapyRevenuePence;
                    return <tr key={adjustment.id}>
                        <td className={cellClass}>
                            {adjustment.providerTestMode ? <Icon icon="Triangle" /> : <NoValue />}
                        </td>
                        <td className={cellClass}>
                            {adjustment.type}
                        </td>
                        <td className={cellClass}>
                            {adjustment.fundedBy ?? <NoValue />}
                        </td>
                        <td className={cellClass}>
                            <DateTime date={adjustment.adjustedAt} hideTime />
                        </td>
                        <td className={cellClass}>
                            {adjustment.changeSms ?? adjustment.changeLeadCredits ?? adjustment.changePlanCredits ?? <NoValue />}
                        </td>
                        <td className={cellClass}>
                            <Money pence={adjustment?.changePence ?? null} />
                        </td>
                        <td className={cellClass}>
                            {revPence ? <Money pence={revPence} colour /> : <NoValue />}
                        </td>
                    </tr>
                })}
                {adjustments.length === 0 && <tr>
                    <td colSpan="6" className="py-2 text-center font-bold text-muted">
                        No data to display 😞
                    </td>
                </tr>}
            </tbody>
        </table>
    </div>
}

const Adjust = ({args, setArgs, adjustOpen, setAdjustOpen}) => {
    return <Modal isOpen={adjustOpen} setIsOpen={setAdjustOpen} title="Adjust Revenue">
        <Form data={args} onSubmit={setArgs} closeModalOnSubmit>
            <Radio name="year" label="Year" options={[2023, 2024, 2025].map(y => ({value: y, label: y}))} />
            <Radio name="month" label="Month" options={months.map((m, i) => ({value: i+1, label: m}))} />
            <SwitchGroup switches={[
                {label: 'Include test accounts', name: 'includeTestAccounts'},
                {label: 'Include non-Soapy Revenue', name: 'includeNonSoapyRevenue'},
                {label: 'Include any change pence', name: 'includeAnyChangePence'},
            ]} />
            <Actions submit={{label: 'Update'}} />
        </Form>
    </Modal>
}

export default Revenue;
